import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { api, type Conversation, type Me, type Message, postMessageStream } from '../api';

const SIDEBAR_LIMIT = 20;

type DisplayMsg = { id: string; role: 'user' | 'assistant' | 'tool' | 'error'; content: string };

export default function Chat({ me }: { me: Me }) {
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [totalConvs, setTotalConvs] = useState(0);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<DisplayMsg[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const convParam = searchParams.get('conv');
  const location = useLocation();
  // The Devis page hands us a target conversation + a pre-filled draft via router state.
  const navState = location.state as { conv?: number; draft?: string } | null;

  function openConv(id: number) {
    setActiveId(id);
    setSidebarOpen(false);
  }

  function reloadSidebar() {
    return api.listConversations({ limit: SIDEBAR_LIMIT }).then((r) => {
      setConvs(r.items);
      setTotalConvs(r.total);
      return r;
    });
  }

  useEffect(() => {
    reloadSidebar().then((r) => {
      const fromState = navState?.conv ?? null;
      const fromUrlRaw = convParam ? Number(convParam) : null;
      const fromUrl = fromUrlRaw && !Number.isNaN(fromUrlRaw) ? fromUrlRaw : null;
      const target = fromState ?? fromUrl;
      if (target) {
        setActiveId(target);
        if (navState?.draft) setInput(navState.draft);
        // Clean the URL / router state once consumed so it doesn't keep overriding navigation.
        setSearchParams({}, { replace: true });
        window.history.replaceState({}, '');
      } else if (r.items.length > 0 && activeId == null) {
        setActiveId(r.items[0]!.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeId == null) {
      setMessages([]);
      return;
    }
    api.listMessages(activeId).then((rows: Message[]) => {
      setMessages(rows.map((r) => ({ id: `m${r.id}`, role: r.role === 'system' ? 'assistant' : r.role, content: r.content })));
    });
  }, [activeId]);

  useEffect(() => {
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight });
  }, [messages]);

  // Auto-grow the composer with its content, up to 8 lines, then scroll.
  // Runs on every input change — incl. programmatic ones (draft prefill, reset after send).
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 21;
    const borders = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const maxHeight =
      lineHeight * 8 + (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0) + borders;
    el.style.height = Math.min(el.scrollHeight + borders, maxHeight) + 'px';
    el.style.overflowY = el.scrollHeight + borders > maxHeight ? 'auto' : 'hidden';
  }, [input]);

  async function newConv() {
    const c = await api.createConversation();
    setConvs((cs) => [c, ...cs].slice(0, SIDEBAR_LIMIT));
    setTotalConvs((n) => n + 1);
    setActiveId(c.id);
    setSidebarOpen(false);
  }

  async function delConv(id: number) {
    if (!confirm('Supprimer cette conversation ?')) return;
    await api.deleteConversation(id);
    setConvs((cs) => cs.filter((c) => c.id !== id));
    setTotalConvs((n) => Math.max(0, n - 1));
    if (activeId === id) setActiveId(null);
  }

  function addFiles(files: FileList | File[] | null) {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).slice(0, 10);
    setAttachments((cur) => [...cur, ...arr].slice(0, 10));
  }
  function removeAttachment(idx: number) {
    setAttachments((cur) => cur.filter((_, i) => i !== idx));
  }
  function onPaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = e.clipboardData?.files;
    if (files && files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  }
  function onDropFiles(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files ?? null);
  }

  async function send(e: FormEvent) {
    e.preventDefault();
    if ((!input.trim() && attachments.length === 0) || streaming) return;
    let convId = activeId;
    if (convId == null) {
      const c = await api.createConversation();
      setConvs((cs) => [c, ...cs].slice(0, SIDEBAR_LIMIT));
      setTotalConvs((n) => n + 1);
      setActiveId(c.id);
      convId = c.id;
    }

    // Upload attachments first (if any) and prefix the message with their paths.
    let content = input;
    if (attachments.length > 0) {
      try {
        const { attachments: saved } = await api.uploadAttachments(convId, attachments);
        const lines = saved.map((a) => `- attachments/${a.name}`).join('\n');
        const prefix = `J'ai joint ${saved.length === 1 ? 'ce document' : 'ces documents'} à mon message (lisibles via l'outil Read depuis le cwd) :\n${lines}\n\n`;
        content = prefix + (input.trim() ? input : '');
      } catch (err: any) {
        setMessages((m) => [...m, { id: `e${Date.now()}`, role: 'error', content: 'Échec de l\'upload : ' + err.message }]);
        return;
      }
    }

    const displayedUserContent = attachments.length > 0
      ? `📎 ${attachments.map((a) => a.name).join(', ')}${input.trim() ? '\n\n' + input : ''}`
      : input;
    const userMsg: DisplayMsg = { id: `u${Date.now()}`, role: 'user', content: displayedUserContent };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setAttachments([]);
    setStreaming(true);

    // Track the currently "open" assistant bubble. A tool_use event closes
    // it so the next text delta starts a fresh bubble below the tool calls —
    // preserves the chronological order text → tools → text → tools → final text.
    let activeAssistantId: string | null = null;

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await postMessageStream(convId, content, (ev) => {
        if (ev.event === 'text') {
          const delta = ev.data.delta ?? '';
          if (activeAssistantId) {
            const id = activeAssistantId;
            setMessages((m) => m.map((x) => (x.id === id ? { ...x, content: x.content + delta } : x)));
          } else {
            const id = `a${Date.now()}-${Math.random()}`;
            activeAssistantId = id;
            setMessages((m) => [...m, { id, role: 'assistant', content: delta }]);
          }
        } else if (ev.event === 'tool_use') {
          activeAssistantId = null;
          setMessages((m) => [...m, { id: `t${Date.now()}-${Math.random()}`, role: 'tool', content: `→ ${ev.data.name}(${JSON.stringify(ev.data.input).slice(0, 200)})` }]);
        } else if (ev.event === 'title') {
          const newTitle = ev.data.title as string;
          setConvs((cs) => cs.map((c) => (c.id === convId ? { ...c, title: newTitle } : c)));
        } else if (ev.event === 'error') {
          setMessages((m) => [...m, { id: `e${Date.now()}`, role: 'error', content: ev.data.message }]);
        }
      }, ac.signal);
    } catch (err: any) {
      // Don't surface AbortError as a message — the user knows they hit Stop.
      if (err?.name !== 'AbortError') {
        setMessages((m) => [...m, { id: `e${Date.now()}`, role: 'error', content: err.message }]);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
      // Refresh conversation list (bumps updated_at order) and reload messages
      // so the persisted assistant content (incl. "[interrompu]" marker if any) shows.
      reloadSidebar();
      if (convId != null) api.listMessages(convId).then((rows: Message[]) => {
        setMessages(rows.map((r) => ({ id: `m${r.id}`, role: r.role === 'system' ? 'assistant' : r.role, content: r.content })));
      });
    }
  }

  function stop() {
    abortRef.current?.abort();
  }

  const activeTitle = convs.find((c) => c.id === activeId)?.title ?? '';

  return (
    <div className={`chat-page ${sidebarOpen ? 'sidebar-open' : ''}`}>
      <button
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label={sidebarOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
      >
        {sidebarOpen ? '×' : '☰'}
      </button>
      <div className="mobile-topbar-title">{activeTitle}</div>
      <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      <aside className="sidebar">
        <header>
          <button onClick={newConv}>+ Nouvelle</button>
        </header>
        <div className="convs">
          {convs.map((c) => (
            <div
              key={c.id}
              className={`conv-item ${c.id === activeId ? 'active' : ''}`}
              onClick={() => openConv(c.id)}
            >
              <span>{c.title}</span>
              <button className="del" onClick={(e) => { e.stopPropagation(); delConv(c.id); }}>×</button>
            </div>
          ))}
          {totalConvs > convs.length && (
            <Link to="/conversations" className="see-all" onClick={() => setSidebarOpen(false)}>
              Toutes les conversations ({totalConvs}) →
            </Link>
          )}
        </div>
        <footer>
          <span>{me.username}{me.is_admin ? ' ★' : ''}</span>
        </footer>
      </aside>
      <main className="main">
        <div className="messages" ref={messagesRef}>
          <div className="messages-inner">
            {messages.length === 0 && activeId == null && (
              <div style={{ textAlign: 'center', color: '#999', marginTop: 80 }}>
                Crée une nouvelle conversation pour commencer.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} className={`msg ${m.role}`}>
                {m.role === 'assistant' ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                ) : m.content}
              </div>
            ))}
            {streaming && (
              <div className="msg assistant thinking">
                <div className="thinking-dots"><span /><span /><span /></div>
              </div>
            )}
          </div>
        </div>
        <div
          className={`input-bar ${dragging ? 'dragging' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDropFiles}
        >
          {attachments.length > 0 && (
            <div className="attachments">
              {attachments.map((f, i) => (
                <span key={i} className="att-chip" title={`${f.name} — ${(f.size / 1024).toFixed(1)} kB`}>
                  <span className="att-name">📎 {f.name}</span>
                  <span className="att-size">{(f.size / 1024).toFixed(0)} kB</span>
                  <button
                    type="button"
                    className="att-remove"
                    onClick={() => removeAttachment(i)}
                    aria-label={`Retirer ${f.name}`}
                    title="Retirer ce document"
                  >
                    ×
                  </button>
                </span>
              ))}
              {attachments.length > 1 && (
                <button type="button" className="att-clear" onClick={() => setAttachments([])}>
                  Tout retirer
                </button>
              )}
            </div>
          )}
          <form onSubmit={send}>
            <button
              type="button"
              className="attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={streaming}
              title="Joindre un document"
            >
              📎
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
            <textarea
              ref={textareaRef}
              placeholder={dragging ? 'Lâche pour joindre le(s) fichier(s)…' : 'Pose ta question à Claude…'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e as any); } }}
              onPaste={onPaste}
              disabled={streaming}
            />
            {streaming ? (
              <button type="button" className="stop-btn" onClick={stop}>Arrêter</button>
            ) : (
              <button disabled={!input.trim() && attachments.length === 0}>Envoyer</button>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
